import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type ThemeName = "lightTheme" | "darkTheme";

/** The <html data-theme> value doubles as the localStorage key. */
const STORAGE_KEY = "data-theme";
/** Written by the old Utilis/getCurrentTheme helper; read once, then migrated. */
const LEGACY_STORAGE_KEY = "theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Full-screen panel that wipes the old theme away; styled in styles/index.scss. */
const WIPE_CLASS = "theme-wipe";
/**
 * Carried by <html> for the length of the wipe. It clears the app-level
 * background fills so the panel — which sits BEHIND every element — is visible
 * through them; header, cards and modals keep their own fills and stay on top.
 */
const WIPING_CLASS = "theme-wiping";
const WIPE_MS = 620;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Paints a panel in the OUTGOING background colour over the whole viewport and
 * slides it out of the way, uncovering the incoming theme from the side the
 * language reads from: right-to-left in LTR, mirrored in RTL (i18n.tsx keeps
 * <html dir> current). Returns a cancel that removes the panel early.
 */
function runThemeWipe(outgoingColor: string): () => void {
  const isRtl = document.documentElement.dir === "rtl";

  const overlay = document.createElement("div");
  overlay.className = isRtl ? `${WIPE_CLASS} ${WIPE_CLASS}--rtl` : WIPE_CLASS;
  overlay.style.background = outgoingColor;
  document.documentElement.classList.add(WIPING_CLASS);
  document.body.appendChild(overlay);

  const remove = () => {
    overlay.remove();
    document.documentElement.classList.remove(WIPING_CLASS);
  };

  try {
    const animation = overlay.animate(
      [
        { offset: 0, transform: "translateX(0)", opacity: 1 },
        {
          offset: 0.55,
          transform: isRtl ? "translateX(48%)" : "translateX(-48%)",
          opacity: 1,
          easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        },
        {
          offset: 1,
          transform: isRtl ? "translateX(100%)" : "translateX(-100%)",
          opacity: 0.88,
        },
      ],
      {
        duration: WIPE_MS,
        // Out-expo: quick to commit, long soft tail — the part that reads fluid.
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "forwards",
      }
    );
    animation.finished.then(remove, remove);
  } catch {
    // No Web Animations support — never leave the panel sitting on screen.
    remove();
  }

  return remove;
}

/** The stored choice, or null when the user has never picked a theme here. */
function readStoredTheme(): ThemeName | null {
  try {
    const stored =
      localStorage.getItem(STORAGE_KEY) ??
      localStorage.getItem(LEGACY_STORAGE_KEY);
    if (stored === "darkTheme" || stored === "lightTheme") {
      return stored;
    }
    return null;
  } catch {
    return null;
  }
}

function readSystemTheme(): ThemeName {
  try {
    return window.matchMedia(DARK_QUERY).matches ? "darkTheme" : "lightTheme";
  } catch {
    return "lightTheme";
  }
}

type ThemeContextValue = {
  theme: ThemeName;
  isDark: boolean;
  /** False while the app is still mirroring the OS setting. */
  isExplicit: boolean;
  setTheme: (theme: ThemeName) => void;
  toggleTheme: () => void;
  /** Drop the saved choice and go back to following the OS setting. */
  useSystemTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Single source of truth for the active theme.
 *
 * Before this existed, App.tsx and Header each held their own useState copy —
 * against two different localStorage keys ("data-theme" and "theme") — so they
 * could disagree about which theme was active.
 *
 * With no saved choice (first visit) the OS `prefers-color-scheme` wins and
 * keeps winning while the user changes it. Nothing is written to localStorage
 * until the user picks a theme; that pick then survives OS changes.
 */
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [storedTheme, setStoredTheme] = useState<ThemeName | null>(
    readStoredTheme
  );
  const [systemTheme, setSystemTheme] = useState<ThemeName>(readSystemTheme);

  const theme = storedTheme ?? systemTheme;

  // The very first paint must not animate — there is no previous theme to wipe
  // away, and index.html already set the attribute pre-paint.
  const isFirstPaint = useRef(true);

  // The only place <html data-theme> is written. The token blocks in
  // styles/index.scss key off this attribute.
  useLayoutEffect(() => {
    const root = document.documentElement;

    if (isFirstPaint.current || prefersReducedMotion()) {
      isFirstPaint.current = false;
      root.setAttribute("data-theme", theme);
      return;
    }

    // Read the outgoing colour BEFORE the attribute flips, or the panel would
    // be painted in the theme it is supposed to be hiding.
    const outgoingColor =
      getComputedStyle(root).getPropertyValue("--bg-app").trim() || "#f0f1f5";

    root.setAttribute("data-theme", theme);

    // Cleanup runs on a second toggle mid-wipe, so panels never stack up.
    return runThemeWipe(outgoingColor);
  }, [theme]);

  // Persist only a deliberate choice — a system-derived theme stays unsaved so
  // the next OS change still applies.
  useEffect(() => {
    try {
      if (storedTheme) {
        localStorage.setItem(STORAGE_KEY, storedTheme);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* private mode / storage disabled — the attribute still applies */
    }
  }, [storedTheme]);

  // Follow the OS while it is what we are showing.
  useEffect(() => {
    let media: MediaQueryList;
    try {
      media = window.matchMedia(DARK_QUERY);
    } catch {
      return;
    }
    const onChange = (event: MediaQueryListEvent) =>
      setSystemTheme(event.matches ? "darkTheme" : "lightTheme");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const setTheme = useCallback(
    (next: ThemeName) => setStoredTheme(next),
    []
  );

  const toggleTheme = useCallback(
    () =>
      setStoredTheme((current) =>
        (current ?? readSystemTheme()) === "darkTheme"
          ? "lightTheme"
          : "darkTheme"
      ),
    []
  );

  const useSystemTheme = useCallback(() => setStoredTheme(null), []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      isDark: theme === "darkTheme",
      isExplicit: storedTheme !== null,
      setTheme,
      toggleTheme,
      useSystemTheme,
    }),
    [theme, storedTheme, setTheme, toggleTheme, useSystemTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
